'use client';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';

type Lang = 'sq' | 'en';

const t = {
  sq: {
    nav: {
      howItWorks: 'Si funksionon',
      faq: 'FAQ',
      contact: 'Kontakt',
      login: 'Hyr',
      register: 'Regjistro biznesin',
    },
    badge: 'Na kontaktoni',
    title: 'Si mund t\'ju ndihmojmë?',
    subtitle: 'Jemi këtu për çdo pyetje rreth shitjeve, mbështetjes teknike ose partneriteteve. Ekipi ynë përgjigjet brenda 24 orëve.',
    types: [
      { icon: '💼', label: 'Pyetje shitjesh', desc: 'Çmime, plane dhe demo' },
      { icon: '🛠️', label: 'Mbështetje teknike', desc: 'Ndihmë me platformën' },
      { icon: '🤝', label: 'Partneritet', desc: 'Bashkëpunim biznesi' },
    ],
    form: {
      title: 'Dërgoni mesazh',
      name: 'Emri i plotë',
      namePlaceholder: 'p.sh. Arta Kelmendi',
      email: 'Email',
      emailPlaceholder: 'arta@biznesi.al',
      phone: 'Numri i telefonit',
      phonePlaceholder: '+355 69 123 4567',
      subject: 'Lloji i kërkesës',
      subjectOptions: [
        { value: '', label: 'Zgjidhni llojin...' },
        { value: 'sales', label: '💼 Pyetje shitjesh' },
        { value: 'support', label: '🛠️ Mbështetje teknike' },
        { value: 'partnership', label: '🤝 Partneritet' },
        { value: 'other', label: '💬 Tjetër' },
      ],
      message: 'Mesazhi',
      messagePlaceholder: 'Shkruani mesazhin tuaj këtu...',
      submit: 'Dërgo mesazhin →',
      submitting: 'Duke dërguar...',
      successTitle: 'Mesazhi u dërgua!',
      successMsg: 'Faleminderit që na kontaktuat. Ekipi ynë do t\'ju përgjigjet brenda 24 orëve.',
      backHome: '← Kthehu në faqen kryesore',
      errors: {
        nameRequired: 'Emri është i detyrueshëm.',
        emailRequired: 'Email-i është i detyrueshëm.',
        emailInvalid: 'Ju lutem vendosni një email të vlefshëm.',
        phoneRequired: 'Numri i telefonit është i detyrueshëm.',
        subjectRequired: 'Ju lutem zgjidhni llojin e kërkesës.',
        messageRequired: 'Mesazhi është i detyrueshëm.',
      },
    },
    info: {
      title: 'Informacione kontakti',
      email: 'info@rezervo-online.al',
      phone: '+355 69 000 0000',
      hours: 'E Hënë – E Premte, 09:00 – 18:00',
      hoursLabel: 'Orari i punës',
      responseLabel: 'Koha e përgjigjes',
      responseTime: 'Brenda 24 orëve',
      followLabel: 'Na ndiqni',
    },
  },
  en: {
    nav: {
      howItWorks: 'How it works',
      faq: 'FAQ',
      contact: 'Contact',
      login: 'Login',
      register: 'Register your business',
    },
    badge: 'Contact us',
    title: 'How can we help you?',
    subtitle: 'We\'re here for any questions about sales, technical support, or partnerships. Our team responds within 24 hours.',
    types: [
      { icon: '💼', label: 'Sales inquiry', desc: 'Pricing, plans and demo' },
      { icon: '🛠️', label: 'Technical support', desc: 'Help with the platform' },
      { icon: '🤝', label: 'Partnership', desc: 'Business collaboration' },
    ],
    form: {
      title: 'Send a message',
      name: 'Full name',
      namePlaceholder: 'e.g. Arta Kelmendi',
      email: 'Email',
      emailPlaceholder: 'arta@business.com',
      phone: 'Phone number',
      phonePlaceholder: '+355 69 123 4567',
      subject: 'Request type',
      subjectOptions: [
        { value: '', label: 'Select type...' },
        { value: 'sales', label: '💼 Sales inquiry' },
        { value: 'support', label: '🛠️ Technical support' },
        { value: 'partnership', label: '🤝 Partnership' },
        { value: 'other', label: '💬 Other' },
      ],
      message: 'Message',
      messagePlaceholder: 'Write your message here...',
      submit: 'Send message →',
      submitting: 'Sending...',
      successTitle: 'Message sent!',
      successMsg: 'Thank you for contacting us. Our team will reply within 24 hours.',
      backHome: '← Back to homepage',
      errors: {
        nameRequired: 'Name is required.',
        emailRequired: 'Email is required.',
        emailInvalid: 'Please enter a valid email.',
        phoneRequired: 'Phone number is required.',
        subjectRequired: 'Please select a request type.',
        messageRequired: 'Message is required.',
      },
    },
    info: {
      title: 'Contact information',
      email: 'info@rezervo-online.al',
      phone: '+355 69 000 0000',
      hours: 'Monday – Friday, 09:00 – 18:00',
      hoursLabel: 'Working hours',
      responseLabel: 'Response time',
      responseTime: 'Within 24 hours',
      followLabel: 'Follow us',
    },
  },
};

export default function ContactPage() {
  const [lang, setLang] = useState<Lang>('sq');
  const [scrolled, setScrolled] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');

  const tr = t[lang];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = tr.form.errors.nameRequired;
    if (!form.email.trim()) e.email = tr.form.errors.emailRequired;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = tr.form.errors.emailInvalid;
    if (!form.phone.trim()) e.phone = tr.form.errors.phoneRequired;
    if (!form.subject) e.subject = tr.form.errors.subjectRequired;
    if (!form.message.trim()) e.message = tr.form.errors.messageRequired;
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setSubmitError('');
    setSubmitting(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setSubmitError(data.error || 'Ndodhi një gabim. Ju lutem provoni përsëri.');
        setSubmitting(false);
        return;
      }
      setSubmitted(true);
    } catch {
      setSubmitError('Ndodhi një gabim. Ju lutem provoni përsëri.');
    }
    setSubmitting(false);
  };

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
  };

  const inputClass = (field: string) =>
    `w-full px-4 py-3 rounded-xl text-sm text-white placeholder-[#4A4060] outline-none transition-all duration-200 ${
      errors[field]
        ? 'border border-red-500/60 bg-[rgba(255,60,60,0.05)] focus:border-red-400'
        : 'border border-[rgba(93,10,244,0.2)] bg-[rgba(255,255,255,0.04)] focus:border-[rgba(93,10,244,0.5)] focus:bg-[rgba(93,10,244,0.06)]'
    }`;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #04020C 0%, #0A0618 50%, #04020C 100%)' }}>
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full opacity-20" style={{ background: 'radial-gradient(circle, rgba(93,10,244,0.4) 0%, transparent 70%)', filter: 'blur(80px)' }} />
        <div className="absolute bottom-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full opacity-15" style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.3) 0%, transparent 70%)', filter: 'blur(80px)' }} />
      </div>

      {/* Nav */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[rgba(10,8,20,0.92)] backdrop-blur-xl border-b border-[rgba(93,10,244,0.15)]' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/" className="flex items-center">
            <Image
              src="/assets/images/LOGO_REZERVO_HORIZONTALE-removebg-preview-1785868391038.png"
              alt="Rezervo Online Logo"
              width={300}
              height={75}
              className="h-20 w-auto object-contain"
            />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            {[
              { label: tr.nav.howItWorks, href: '/#si-funksionon' },
              { label: tr.nav.faq, href: '/#faq' },
              { label: tr.nav.contact, href: '/contact' },
            ].map((item) => (
              <Link
                key={item.label}
                href={item.href}
                className="text-sm text-[#A89EC4] hover:text-white transition-colors duration-200 font-medium"
              >
                {item.label}
              </Link>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-3">
            <div className="flex items-center rounded-xl overflow-hidden border border-[rgba(93,10,244,0.3)] bg-[rgba(93,10,244,0.08)]">
              <button onClick={() => setLang('sq')} className={`px-3 py-1.5 text-xs font-semibold transition-all duration-200 ${lang === 'sq' ? 'bg-[rgba(93,10,244,0.5)] text-white' : 'text-[#A89EC4] hover:text-white'}`}>SQ</button>
              <button onClick={() => setLang('en')} className={`px-3 py-1.5 text-xs font-semibold transition-all duration-200 ${lang === 'en' ? 'bg-[rgba(93,10,244,0.5)] text-white' : 'text-[#A89EC4] hover:text-white'}`}>EN</button>
            </div>
            <Link href="/#register" className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-[#5D0AF4] to-[#7C3AED] text-white hover:opacity-90 transition-all duration-200 shadow-[0_4px_20px_rgba(93,10,244,0.4)]">
              {tr.nav.register}
            </Link>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="relative z-10 pt-36 pb-24 px-6">
        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold mb-6" style={{ background: 'rgba(93,10,244,0.15)', border: '1px solid rgba(93,10,244,0.3)', color: '#A89EC4' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#5D0AF4] animate-pulse" />
              {tr.badge}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {tr.title}
            </h1>
            <p className="text-[#A89EC4] text-lg max-w-2xl mx-auto leading-relaxed">
              {tr.subtitle}
            </p>
          </div>

          {/* Type cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
            {tr.types.map((type) => (
              <div
                key={type.label}
                className="rounded-2xl p-5 flex items-center gap-4 transition-all duration-200 hover:scale-[1.02]"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(93,10,244,0.15)' }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0" style={{ background: 'rgba(93,10,244,0.12)', border: '1px solid rgba(93,10,244,0.2)' }}>
                  {type.icon}
                </div>
                <div>
                  <div className="text-white text-sm font-semibold">{type.label}</div>
                  <div className="text-[#6B6080] text-xs mt-0.5">{type.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Two-column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

            {/* Form — 3 cols */}
            <div className="lg:col-span-3">
              <div
                className="rounded-3xl p-8"
                style={{
                  background: 'linear-gradient(145deg, rgba(20,12,45,0.8) 0%, rgba(10,6,25,0.9) 100%)',
                  border: '1px solid rgba(93,10,244,0.2)',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
                }}
              >
                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center text-3xl mb-6" style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)' }}>
                      ✅
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-3">{tr.form.successTitle}</h2>
                    <p className="text-[#A89EC4] text-sm leading-relaxed max-w-sm mb-8">{tr.form.successMsg}</p>
                    <Link href="/" className="text-sm text-[#7C3AED] hover:text-[#A78BFA] transition-colors duration-200 font-medium">
                      {tr.form.backHome}
                    </Link>
                  </div>
                ) : (
                  <>
                    <h2 className="text-xl font-bold text-white mb-6">{tr.form.title}</h2>
                    <form onSubmit={handleSubmit} noValidate className="space-y-5">
                      {/* Name + Email */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-[#A89EC4] mb-2">{tr.form.name}</label>
                          <input
                            type="text"
                            value={form.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            placeholder={tr.form.namePlaceholder}
                            className={inputClass('name')}
                          />
                          {errors.name && <p className="text-red-400 text-xs mt-1.5">{errors.name}</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-[#A89EC4] mb-2">{tr.form.email}</label>
                          <input
                            type="email"
                            value={form.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            placeholder={tr.form.emailPlaceholder}
                            className={inputClass('email')}
                          />
                          {errors.email && <p className="text-red-400 text-xs mt-1.5">{errors.email}</p>}
                        </div>
                      </div>

                      {/* Phone + Subject */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-[#A89EC4] mb-2">{tr.form.phone}</label>
                          <input
                            type="tel"
                            value={form.phone}
                            onChange={(e) => handleChange('phone', e.target.value)}
                            placeholder={tr.form.phonePlaceholder}
                            className={inputClass('phone')}
                          />
                          {errors.phone && <p className="text-red-400 text-xs mt-1.5">{errors.phone}</p>}
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-[#A89EC4] mb-2">{tr.form.subject}</label>
                          <select
                            value={form.subject}
                            onChange={(e) => handleChange('subject', e.target.value)}
                            className={`${inputClass('subject')} cursor-pointer`}
                            style={{ appearance: 'none' }}
                          >
                            {tr.form.subjectOptions.map((opt) => (
                              <option key={opt.value} value={opt.value} style={{ background: '#0A0618', color: opt.value ? '#fff' : '#4A4060' }}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          {errors.subject && <p className="text-red-400 text-xs mt-1.5">{errors.subject}</p>}
                        </div>
                      </div>

                      {/* Message */}
                      <div>
                        <label className="block text-xs font-semibold text-[#A89EC4] mb-2">{tr.form.message}</label>
                        <textarea
                          value={form.message}
                          onChange={(e) => handleChange('message', e.target.value)}
                          placeholder={tr.form.messagePlaceholder}
                          rows={5}
                          className={`${inputClass('message')} resize-none`}
                        />
                        {errors.message && <p className="text-red-400 text-xs mt-1.5">{errors.message}</p>}
                      </div>

                      {submitError && (
                        <div className="rounded-xl px-4 py-3 text-sm text-red-300" style={{ background: 'rgba(255,60,60,0.08)', border: '1px solid rgba(255,60,60,0.25)' }}>
                          {submitError}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full py-3.5 rounded-xl text-sm font-semibold text-white transition-all duration-200 disabled:opacity-60"
                        style={{
                          background: submitting ? 'rgba(93,10,244,0.5)' : 'linear-gradient(135deg, #5D0AF4 0%, #7C3AED 100%)',
                          boxShadow: submitting ? 'none' : '0 4px_20px rgba(93,10,244,0.4)',
                        }}
                      >
                        {submitting ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            {tr.form.submitting}
                          </span>
                        ) : tr.form.submit}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>

            {/* Info panel — 2 cols */}
            <div className="lg:col-span-2 flex flex-col gap-5">
              <div
                className="rounded-3xl p-7 flex-1"
                style={{
                  background: 'linear-gradient(145deg, rgba(20,12,45,0.8) 0%, rgba(10,6,25,0.9) 100%)',
                  border: '1px solid rgba(93,10,244,0.2)',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
                }}
              >
                <h3 className="text-lg font-bold text-white mb-6">{tr.info.title}</h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-base flex-shrink-0" style={{ background: 'rgba(93,10,244,0.12)', border: '1px solid rgba(93,10,244,0.2)' }}>
                      ✉️
                    </div>
                    <div>
                      <div className="text-[#6B6080] text-xs mb-1">Email</div>
                      <a href={`mailto:${tr.info.email}`} className="text-white text-sm font-medium hover:text-[#A78BFA] transition-colors duration-200">
                        {tr.info.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-base flex-shrink-0" style={{ background: 'rgba(93,10,244,0.12)', border: '1px solid rgba(93,10,244,0.2)' }}>
                      📞
                    </div>
                    <div>
                      <div className="text-[#6B6080] text-xs mb-1">{tr.nav.contact}</div>
                      <a href={`tel:${tr.info.phone}`} className="text-white text-sm font-medium hover:text-[#A78BFA] transition-colors duration-200">
                        {tr.info.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-base flex-shrink-0" style={{ background: 'rgba(93,10,244,0.12)', border: '1px solid rgba(93,10,244,0.2)' }}>
                      🕐
                    </div>
                    <div>
                      <div className="text-[#6B6080] text-xs mb-1">{tr.info.hoursLabel}</div>
                      <div className="text-white text-sm font-medium">{tr.info.hours}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-base flex-shrink-0" style={{ background: 'rgba(93,10,244,0.12)', border: '1px solid rgba(93,10,244,0.2)' }}>
                      ⚡
                    </div>
                    <div>
                      <div className="text-[#6B6080] text-xs mb-1">{tr.info.responseLabel}</div>
                      <div className="text-white text-sm font-medium">{tr.info.responseTime}</div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-[rgba(93,10,244,0.15)]">
                  <div className="text-[#6B6080] text-xs mb-3">{tr.info.followLabel}</div>
                  <div className="flex gap-3">
                    <a
                      href="https://instagram.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-[#A89EC4] hover:text-white transition-all duration-200 hover:scale-110"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(93,10,244,0.2)' }}
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                    </a>
                    <a
                      href="https://facebook.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-[#A89EC4] hover:text-white transition-all duration-200 hover:scale-110"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(93,10,244,0.2)' }}
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-[rgba(93,10,244,0.1)] py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[#4A4060] text-xs">
            {lang === 'sq' ? '© 2026 Rezervo Online. Të gjitha të drejtat e rezervuara.' : '© 2026 Rezervo Online. All rights reserved.'}
          </p>
          <Link href="/" className="text-xs text-[#6B6080] hover:text-[#A89EC4] transition-colors duration-200">
            {tr.form.backHome}
          </Link>
        </div>
      </footer>
    </div>
  );
}
